import type { Key } from "react";

import type { Vero1Severity } from "shared/interfaces/api/vero1";
import { Vero1ItemType, Vero1RowKind } from "shared/interfaces/api/vero1";

import { effectiveTone, lumenTone } from "./formatItemValue";

export type OrderRowsBy = "lumen" | "effective";

export interface OrderableRow {
  itemType?: Vero1ItemType | string;
  rowKind?: Vero1RowKind | string;
  criterionKey: string;
  label?: string;
  application: Key | null;
  originalSeverity: Vero1Severity | null;
  overrideSeverity?: Vero1Severity | null;
}

const BAND_RANK: Record<Vero1Severity, number> = { fail: 0, warn: 1, pass: 2 };
const NO_VERDICT_RANK = 3;
const FACT_RANK = 4;

const bandRank = (row: OrderableRow, by: OrderRowsBy): number => {
  if (row.rowKind === Vero1RowKind.Fact) return FACT_RANK;
  const tone = by === "lumen" ? lumenTone(row) : effectiveTone(row);
  return tone === null ? NO_VERDICT_RANK : BAND_RANK[tone];
};

const criterionOf = (row: OrderableRow): string =>
  row.itemType === Vero1ItemType.Custom
    ? `custom:${row.label ?? ""}`
    : row.criterionKey;

const firstAppearance = <T>(values: T[]): Map<T, number> => {
  const order = new Map<T, number>();
  values.forEach((value) => {
    if (!order.has(value)) order.set(value, order.size);
  });
  return order;
};

export const orderRowsInGroup = <T extends OrderableRow>(
  rows: T[],
  { by }: { by: OrderRowsBy }
): T[] => {
  const criterionOrder = firstAppearance(rows.map(criterionOf));
  const applicationOrder = firstAppearance(
    rows
      .filter((row) => row.application !== null)
      .map((row) => String(row.application))
  );
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const band = bandRank(a.row, by) - bandRank(b.row, by);
      if (band !== 0) return band;
      const criterion =
        (criterionOrder.get(criterionOf(a.row)) ?? 0) -
        (criterionOrder.get(criterionOf(b.row)) ?? 0);
      if (criterion !== 0) return criterion;
      const aDeal = a.row.application === null ? 0 : 1;
      const bDeal = b.row.application === null ? 0 : 1;
      if (aDeal !== bDeal) return aDeal - bDeal;
      const applicant =
        (applicationOrder.get(String(a.row.application)) ?? 0) -
        (applicationOrder.get(String(b.row.application)) ?? 0);
      if (applicant !== 0) return applicant;
      return a.index - b.index;
    })
    .map(({ row }) => row);
};
